import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useTenantId } from './useTenantId';

export interface PartnerDocument {
  id: string;
  company_id: string;
  tenant_id: string;
  document_type: string;
  document_category: string;
  file_path: string;
  file_name: string;
  file_size_bytes: number;
  mime_type: string;
  document_number?: string;
  issue_date?: string;
  expiry_date?: string;
  issuing_authority?: string;
  verification_status: 'pending' | 'verified' | 'rejected' | 'expired';
  verified_by_user_id?: string;
  verified_at?: string;
  verification_notes?: string;
  extracted_data?: any;
  extraction_confidence?: number;
  uploaded_by_user_id?: string;
  uploaded_by_contractor_user_id?: string;
  is_required_for_prequalification: boolean;
  created_at: string;
  updated_at: string;
}

export interface UploadDocumentData {
  document_type: string;
  document_category: string;
  file: File;
  document_number?: string;
  issue_date?: string;
  expiry_date?: string;
  issuing_authority?: string;
  is_required_for_prequalification?: boolean;
  extracted_data?: any;
  extraction_confidence?: number;
}

export function usePartnerDocuments(companyId: string | null) {
  const queryClient = useQueryClient();
  const { tenantId } = useTenantId();

  const { data: documents = [], isLoading } = useQuery({
    queryKey: ['partner-documents', companyId],
    queryFn: async () => {
      if (!companyId) return [];
      
      const { data, error } = await supabase
        .from('partner_documents')
        .select('*')
        .eq('company_id', companyId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as PartnerDocument[];
    },
    enabled: !!companyId,
  });

  const uploadDocument = useMutation({
    mutationFn: async ({ companyId, data }: { companyId: string; data: UploadDocumentData }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Generate unique file path (standardized format without document_type folder)
      const timestamp = Date.now();
      const filePath = `${tenantId}/${companyId}/${timestamp}-${data.file.name}`;

      // Upload file to storage
      const { error: uploadError } = await supabase.storage
        .from('partner-documents')
        .upload(filePath, data.file);

      if (uploadError) throw uploadError;

      // Verify file was uploaded successfully (Option C: Storage verification)
      const { data: verifyData, error: verifyError } = await supabase.storage
        .from('partner-documents')
        .list(`${tenantId}/${companyId}`, {
          search: `${timestamp}-${data.file.name}`
        });

      if (verifyError || !verifyData || verifyData.length === 0) {
        // Cleanup: Try to remove the potentially corrupted upload
        await supabase.storage.from('partner-documents').remove([filePath]);
        throw new Error('File upload verification failed. Please try again.');
      }

      // Create database record
      const insertData: any = {
        company_id: companyId,
        tenant_id: tenantId,
        document_type: data.document_type,
        document_category: data.document_category,
        file_path: filePath,
        file_name: data.file.name,
        file_size_bytes: data.file.size,
        mime_type: data.file.type,
        is_required_for_prequalification: data.is_required_for_prequalification || false,
        uploaded_by_user_id: user.id,
        verification_status: 'pending',
      };

      if (data.document_number) insertData.document_number = data.document_number;
      if (data.issue_date) insertData.issue_date = data.issue_date;
      if (data.expiry_date) insertData.expiry_date = data.expiry_date;
      if (data.issuing_authority) insertData.issuing_authority = data.issuing_authority;
      if (data.extracted_data) insertData.extracted_data = data.extracted_data;
      if (data.extraction_confidence !== undefined) insertData.extraction_confidence = data.extraction_confidence;

      const { data: document, error: dbError } = await supabase
        .from('partner_documents')
        .insert(insertData)
        .select()
        .single();

      if (dbError) throw dbError;
      return document;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['partner-documents', companyId] });
      toast.success('Document uploaded successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to upload document');
    },
  });

  const downloadDocument = useMutation({
    mutationFn: async (documentId: string) => {
      const document = documents.find(d => d.id === documentId);
      if (!document) throw new Error('Document not found');

      const { data, error } = await supabase.storage
        .from('partner-documents')
        .createSignedUrl(document.file_path, 300); // 5 minutes

      if (error) throw error;
      
      // Open in new tab
      window.open(data.signedUrl, '_blank');
      return data.signedUrl;
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to download document');
    },
  });

  const updateDocument = useMutation({
    mutationFn: async ({ documentId, updates }: { documentId: string; updates: Partial<PartnerDocument> }) => {
      const { data, error } = await supabase
        .from('partner_documents')
        .update(updates)
        .eq('id', documentId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['partner-documents', companyId] });
      toast.success('Document updated successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to update document');
    },
  });

  const verifyDocument = useMutation({
    mutationFn: async ({ 
      documentId, 
      status, 
      notes,
      verifiedData,
      manualVerifications
    }: { 
      documentId: string; 
      status: 'verified' | 'rejected'; 
      notes?: string;
      verifiedData?: any;
      manualVerifications?: any;
    }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Update document verification status
      const { data: doc, error: docError } = await supabase
        .from('partner_documents')
        .update({
          verification_status: status,
          verified_by_user_id: user.id,
          verified_at: new Date().toISOString(),
          verification_notes: notes,
          extracted_data: status === 'verified' && verifiedData ? {
            ...verifiedData,
            manual_verifications: manualVerifications
          } : undefined,
        })
        .eq('id', documentId)
        .select('company_id, document_type, extracted_data')
        .single();

      if (docError) throw docError;

      // If verified, sync extracted data to company record
      if (status === 'verified' && doc && companyId) {
        const dataToSync = verifiedData || doc.extracted_data || {};
        const companyUpdates: any = {};

        // Map extracted fields to company fields based on document type
        const docType = doc.document_type?.toLowerCase() || '';

        // Insurance documents
        if (docType.includes('general liability') || docType.includes('liability insurance')) {
          if (dataToSync.per_occurrence_limit) companyUpdates.insurance_general_liability_amount = dataToSync.per_occurrence_limit;
          if (dataToSync.aggregate_limit) companyUpdates.insurance_general_liability_amount = Math.max(companyUpdates.insurance_general_liability_amount || 0, dataToSync.aggregate_limit);
          if (dataToSync.expiry_date) companyUpdates.insurance_expiry_date = dataToSync.expiry_date;
          if (dataToSync.issuing_authority) companyUpdates.insurance_carrier = dataToSync.issuing_authority;
        }
        
        if (docType.includes('workers') || docType.includes('compensation')) {
          if (dataToSync.each_accident_limit) companyUpdates.insurance_workers_comp_amount = dataToSync.each_accident_limit;
          if (dataToSync.expiry_date && !companyUpdates.insurance_expiry_date) companyUpdates.insurance_expiry_date = dataToSync.expiry_date;
          if (dataToSync.issuing_authority && !companyUpdates.insurance_carrier) companyUpdates.insurance_carrier = dataToSync.issuing_authority;
        }
        
        if (docType.includes('umbrella')) {
          if (dataToSync.aggregate_limit) companyUpdates.insurance_umbrella_amount = dataToSync.aggregate_limit;
          if (dataToSync.expiry_date && !companyUpdates.insurance_expiry_date) companyUpdates.insurance_expiry_date = dataToSync.expiry_date;
        }

        // License
        if (docType.includes('license')) {
          if (dataToSync.license_number || dataToSync.document_number) {
            companyUpdates.license_number = dataToSync.license_number || dataToSync.document_number;
          }
          if (dataToSync.license_classification) companyUpdates.license_type = dataToSync.license_classification;
          if (dataToSync.license_state) companyUpdates.license_state = dataToSync.license_state;
          if (dataToSync.expiry_date) companyUpdates.license_expiry_date = dataToSync.expiry_date;
        }

        // W-9
        if (docType.includes('w-9') || docType.includes('w9')) {
          if (dataToSync.tax_id) companyUpdates.federal_tax_id = dataToSync.tax_id;
        }

        // Bonding
        if (docType.includes('bond')) {
          if (dataToSync.aggregate_capacity) companyUpdates.bond_capacity = dataToSync.aggregate_capacity;
          if (dataToSync.surety_company_name) companyUpdates.bonding_company = dataToSync.surety_company_name;
        }

        // Update company record if we have any updates
        if (Object.keys(companyUpdates).length > 0) {
          companyUpdates.updated_at = new Date().toISOString();
          
          const { error: companyError } = await supabase
            .from('companies')
            .update(companyUpdates)
            .eq('id', companyId);

          if (companyError) {
            console.error('Error syncing to company record:', companyError);
            // Don't throw - verification should still succeed even if sync fails
            toast.warning('Document verified, but some data could not be synced to company record');
          }
        }

        // Check if all required documents are now verified and update company status
        try {
          // Get tenant_id from company record
          const { data: company } = await supabase
            .from('companies')
            .select('tenant_id')
            .eq('id', companyId)
            .single();

          if (company?.tenant_id) {
            // Fetch tenant settings
            const { data: tenantData } = await supabase
              .from('tenants')
              .select('settings')
              .eq('id', company.tenant_id)
              .single();

            const partnerSettings = (tenantData?.settings as any)?.partner_settings;
            const documentTypes = partnerSettings?.document_types;
            
            if (documentTypes) {
              const requiredTypes = documentTypes.filter((dt: any) => dt.required_for_prequalification);
              
              const { data: allDocuments } = await supabase
                .from('partner_documents')
                .select('*')
                .eq('company_id', companyId);

              if (allDocuments && requiredTypes.length > 0) {
                // Check if all required documents are verified and not expired
                const allRequiredVerified = requiredTypes.every((reqType: any) => {
                  const matchingDoc = allDocuments.find(
                    (d: any) => (d.document_type === reqType.name || d.document_type === reqType.id) && 
                           d.verification_status === 'verified'
                  );
                  
                  if (!matchingDoc) return false;
                  
                  // Check if expired
                  if (matchingDoc.expiry_date) {
                    const expiryDate = new Date(matchingDoc.expiry_date);
                    if (expiryDate < new Date()) return false;
                  }
                  
                  return true;
                });

                // Update company prequalification status if all documents are verified
                if (allRequiredVerified) {
                  await supabase
                    .from('companies')
                    .update({
                      prequalification_status: 'approved',
                      prequalification_date: new Date().toISOString(),
                      updated_at: new Date().toISOString(),
                    })
                    .eq('id', companyId);
                }
              }
            }
          }
        } catch (error) {
          console.error('Error checking prequalification status:', error);
          // Don't throw - verification should still succeed
        }
      }

      return doc;
    },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['partner-documents', companyId] });
        queryClient.invalidateQueries({ queryKey: ['contractor-documents', companyId] });
        queryClient.invalidateQueries({ queryKey: ['companies'] });
        queryClient.invalidateQueries({ queryKey: ['company', companyId] });
        toast.success('Document verified and data synced to company record');
      },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to verify document');
    },
  });

  const deleteDocument = useMutation({
    mutationFn: async (documentId: string) => {
      const document = documents.find(d => d.id === documentId);
      if (!document) throw new Error('Document not found');

      // Delete from storage
      const { error: storageError } = await supabase.storage
        .from('partner-documents')
        .remove([document.file_path]);

      if (storageError) throw storageError;

      // Delete from database
      const { error: dbError } = await supabase
        .from('partner_documents')
        .delete()
        .eq('id', documentId);

      if (dbError) throw dbError;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['partner-documents', companyId] });
      toast.success('Document deleted successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to delete document');
    },
  });

  const uploadMultiTypeDocument = useMutation({
    mutationFn: async ({
      file,
      policies,
    }: {
      file: File;
      policies: Array<{
        document_type_id: string;
        document_type_name: string;
        document_number?: string;
        issue_date?: string;
        expiry_date?: string;
        issuing_authority?: string;
        confidence: number;
        additional_info?: Record<string, any>;
        coverage_amount?: string;
        holder_name?: string;
      }>;
    }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Fetch partner settings to get document type categories
      const { data: settingsData } = await supabase
        .from('tenants')
        .select('settings')
        .eq('id', tenantId)
        .single();

      const partnerSettings = (settingsData?.settings as any)?.partner_settings;
      const documentTypes = partnerSettings?.document_types || [];

      // Upload file once
      const timestamp = Date.now();
      const filePath = `${tenantId}/${companyId}/${timestamp}-${file.name}`;

      const { error: uploadError } = await supabase.storage
        .from('partner-documents')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      // Verify file was uploaded successfully (Option C: Storage verification)
      const { data: verifyData, error: verifyError } = await supabase.storage
        .from('partner-documents')
        .list(`${tenantId}/${companyId}`, {
          search: `${timestamp}-${file.name}`
        });

      if (verifyError || !verifyData || verifyData.length === 0) {
        // Cleanup: Try to remove the potentially corrupted upload
        await supabase.storage.from('partner-documents').remove([filePath]);
        throw new Error('File upload verification failed. Please try again.');
      }

      // Create multiple document records
      const records = policies.map(policy => {
        // Look up the correct category from settings
        // Try matching by ID first, then by name (in case AI returns name instead of ID)
        let documentType = documentTypes.find((dt: any) => dt.id === policy.document_type_id);
        
        if (!documentType) {
          // Fallback: try matching by name
          documentType = documentTypes.find((dt: any) => 
            dt.name.toLowerCase() === policy.document_type_id?.toLowerCase()
          );
        }
        
        // Get category and normalize it
        let category = documentType?.category;
        
        // Normalize: trim whitespace and convert to lowercase
        if (category) {
          category = category.trim().toLowerCase();
        }
        
        // Validate against allowed categories
        const validCategories = ['insurance', 'license', 'financial', 'certification', 'legal', 'safety', 'other'];
        if (!category || !validCategories.includes(category)) {
          console.warn('Invalid or missing category, defaulting to "other":', {
            input_id: policy.document_type_id,
            found_type: documentType,
            invalid_category: category
          });
          category = 'other';
        }
        
        console.log('Document type lookup:', {
          input_id: policy.document_type_id,
          input_name: policy.document_type_name,
          found_type: documentType,
          final_category: category
        });

        return {
          company_id: companyId,
          tenant_id: tenantId,
          document_type: documentType?.id || policy.document_type_id,
          document_category: category,
          file_path: filePath,
          file_name: file.name,
          file_size_bytes: file.size,
          mime_type: file.type,
          document_number: policy.document_number,
          issue_date: policy.issue_date,
          expiry_date: policy.expiry_date,
          issuing_authority: policy.issuing_authority,
          extracted_data: {
            // Include all detected fields for verification
            document_number: policy.document_number,
            issue_date: policy.issue_date,
            expiry_date: policy.expiry_date,
            issuing_authority: policy.issuing_authority,
            coverage_amount: policy.coverage_amount,
            holder_name: policy.holder_name,
            ...policy.additional_info,
          },
          extraction_confidence: policy.confidence,
          verification_status: 'pending',
          uploaded_by_user_id: user.id,
          is_required_for_prequalification: true,
        };
      });

      const { data, error } = await supabase
        .from('partner_documents')
        .insert(records)
        .select();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['partner-documents', companyId] });
      queryClient.invalidateQueries({ queryKey: ['contractor-documents'] });
      toast.success(`Created ${data?.length || 0} documents from certificate`);
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to upload documents');
    },
  });

  return {
    documents,
    isLoading,
    uploadDocument: uploadDocument.mutateAsync,
    uploadMultiTypeDocument: uploadMultiTypeDocument.mutateAsync,
    downloadDocument: downloadDocument.mutateAsync,
    updateDocument: updateDocument.mutateAsync,
    verifyDocument: verifyDocument.mutateAsync,
    deleteDocument: deleteDocument.mutateAsync,
  };
}
